/**
 * Hypes PSI — Qualitative Rubric Definitions
 *
 * Maps the 6 qualitative parameters (ecosystem, risk, social,
 * certifications, governance, economic) to user-friendly maturity-based
 * dropdown options. Each option maps to an internal score of 2, 5, or 10.
 *
 * Mirrors the pattern used by the existing Data Transparency dropdown.
 * NOTE: Backend calculation logic is UNCHANGED. Frontend simply sends the
 * numeric value (2/5/10) as it always did for these parameters.
 */

export const QUALITATIVE_PARAMS = [
  "ecosystem",
  "risk",
  "social",
  "certifications",
  "governance",
  "economic",
];

export const isQualitative = (paramKey) => QUALITATIVE_PARAMS.includes(paramKey);

/**
 * Each rubric uses 3 maturity levels mapped to 2 / 5 / 10.
 *  2  = No formal policy exists
 *  5  = Policy exists but implementation or monitoring is partial/limited
 * 10  = Strong policy with implementation, monitoring, targets & reporting
 */
export const QUALITATIVE_RUBRICS = {
  ecosystem: {
    label: "Ecosystem Impact",
    options: [
      { value: 2,  label: "No ecosystem management practices" },
      { value: 5,  label: "Basic environmental policies with limited implementation" },
      { value: 10, label: "Comprehensive ecosystem impact management with targets and reporting" },
    ],
  },
  risk: {
    label: "Operational Risk",
    options: [
      { value: 2,  label: "No formal operational risk management" },
      { value: 5,  label: "Risk register exists but monitoring and mitigation are partial" },
      { value: 10, label: "Strong risk framework with monitoring, mitigation targets and reporting" },
    ],
  },
  social: {
    label: "Social Responsibility",
    options: [
      { value: 2,  label: "No formal social responsibility policy" },
      { value: 5,  label: "Policy exists but implementation across the value chain is limited" },
      { value: 10, label: "Comprehensive social policy with implementation, monitoring and disclosures" },
    ],
  },
  certifications: {
    label: "Certifications & Ethics",
    options: [
      { value: 2,  label: "No formal certifications or ethics framework" },
      { value: 5,  label: "Some certifications or codes in place but limited verification" },
      { value: 10, label: "Recognised third-party certifications with regular audits and reporting" },
    ],
  },
  governance: {
    label: "Governance",
    options: [
      { value: 2,  label: "No formal governance structure for sustainability" },
      { value: 5,  label: "Basic governance exists but oversight and accountability are partial" },
      { value: 10, label: "Strong governance with board oversight, targets, monitoring and reporting" },
    ],
  },
  economic: {
    label: "Economic Viability",
    options: [
      { value: 2,  label: "No formal economic-sustainability assessment" },
      { value: 5,  label: "Some financial analysis exists but lifecycle economics are partial" },
      { value: 10, label: "Comprehensive lifecycle economic management with targets and reporting" },
    ],
  },
};

/**
 * Given a numeric score (any number), pick the closest rubric option.
 * Used when pre-populating dropdowns from previously-saved numeric inputs.
 * Returns the option's value (2/5/10).
 */
export function nearestRubricValue(paramKey, raw) {
  const r = QUALITATIVE_RUBRICS[paramKey];
  if (!r) return raw;
  const n = Number(raw);
  if (!Number.isFinite(n) || n === 0) return null; // unset
  // Snap to nearest of {2, 5, 10}
  const buckets = [2, 5, 10];
  let best = buckets[0]; let bestDist = Math.abs(n - best);
  for (const b of buckets.slice(1)) {
    const d = Math.abs(n - b);
    if (d < bestDist) { best = b; bestDist = d; }
  }
  return best;
}
